import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { RecipientOtpApiService } from '../../shared/services/auth/recipient-otp-api.service';


@Injectable({ providedIn: 'root' })
export class SessionInactivityService {
    private readonly recipientOtpApiService = inject(RecipientOtpApiService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private readonly INACTIVITY_WARNING_TIME = 55 * 60;
    private readonly LOGOUT_TIME = 60 * 60;
    private readonly WARNING_DURATION = 5 * 60;
    private readonly KEEP_ALIVE_QUEUE_DELAY = 4 * 60;
    private readonly TIMER_TICK = 1;

    private readonly secondsOfInactivity = signal<number>(0);
    private readonly hasQueuedKeepAlive = signal<boolean>(false);
    private isTracking = false;

    private inactivityTimerId: number | null = null;
    private keepAliveTimerId: number | null = null;

    readonly isWarningActive = computed(() => {
        const inactivity = this.secondsOfInactivity();
        return inactivity >= this.INACTIVITY_WARNING_TIME && inactivity < this.LOGOUT_TIME;
    });

    readonly countdownSeconds = computed(() => {
        const inactivity = this.secondsOfInactivity();
        if (inactivity < this.INACTIVITY_WARNING_TIME) {
            return 0;
        }

        const elapsed = inactivity - this.INACTIVITY_WARNING_TIME;
        const remaining = this.WARNING_DURATION - elapsed;
        return Math.max(0, remaining);
    });

    readonly countdownDisplay = computed(() => {
        const seconds = this.countdownSeconds();
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    });

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.cleanup();
        });
    }

    startTracking(): void {
        if (this.isTracking) {
            return;
        }

        this.isTracking = true;
        this.secondsOfInactivity.set(0);
        this.hasQueuedKeepAlive.set(false);
        this.startInactivityTimer();
    }

    resetActivity(): void {
        if (!this.isTracking) {
            return;
        }

        this.secondsOfInactivity.set(0);

        if (!this.hasQueuedKeepAlive()) {
            this.queueKeepAlive();
        }
    }

    logout(): void {
        this.cleanup();
        this.recipientOtpApiService
            .logout()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                complete: () => {
                    this.router.navigate(['/login']);
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private startInactivityTimer(): void {
        this.inactivityTimerId = window.setInterval(() => {
            const current = this.secondsOfInactivity();
            this.secondsOfInactivity.set(current + this.TIMER_TICK);

            if (current + this.TIMER_TICK >= this.LOGOUT_TIME) {
                this.logout();
            }
        }, this.TIMER_TICK * 1000);
    }

    private queueKeepAlive(): void {
        this.hasQueuedKeepAlive.set(true);

        this.keepAliveTimerId = window.setTimeout(() => {
            this.fireKeepAlive();

            if (this.secondsOfInactivity() < this.KEEP_ALIVE_QUEUE_DELAY) {
                this.queueKeepAlive();
            } else {
                this.hasQueuedKeepAlive.set(false);
            }
        }, this.KEEP_ALIVE_QUEUE_DELAY * 1000);
    }

    private fireKeepAlive(): void {
        this.recipientOtpApiService
            .keepAlive()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (err) => {
                    console.error('Keep-alive failed:', err);
                }
            });
    }

    private cleanup(): void {
        if (this.inactivityTimerId !== null) {
            clearInterval(this.inactivityTimerId);
            this.inactivityTimerId = null;
        }

        if (this.keepAliveTimerId !== null) {
            clearTimeout(this.keepAliveTimerId);
            this.keepAliveTimerId = null;
        }

        this.isTracking = false;
    }
}
